/** Global playlist management */

import type { Media, Persisted } from '@tunasong/schemas'
import { useEffect } from 'react'
import invariant from 'tiny-invariant'
import { useGlobalPlayerState } from './global-player-state.hook.js'
import { usePlayerControl } from './player-control.hook.js'
import { playlistManager } from './playlist-manager.js'

export const useGlobalPlayer = ({
  initialPlaylist,
  sourceEntityId,
  playbackRate,
}: { initialPlaylist?: Persisted<Media>[]; sourceEntityId?: string; playbackRate?: number } = {}) => {
  const { playlist, setPlayList, playerRef, setActive, active } = useGlobalPlayerState()

  useEffect(() => {
    if (!initialPlaylist) {
      return
    }

    // Is it the same playlist or empty initial playlist? If so, do nothing
    if (
      initialPlaylist.length === 0 ||
      (playlist.length === initialPlaylist.length && playlist.every((m, i) => m.media.id === initialPlaylist[i].id))
    ) {
      return
    }

    setPlayList(initialPlaylist.map(media => ({ media, sourceEntityId })))

    // If the currently active track is in the new playlist, keep it active
    const activeInNewPlaylist = active && initialPlaylist.some(m => m.id === active.media.id)
    if (!activeInNewPlaylist) {
      setActive({ media: initialPlaylist[0], sourceEntityId })
    }
  }, [active, initialPlaylist, playlist, setActive, setPlayList, sourceEntityId])

  const handleSelect = (num: number) => {
    setActive(playlist[num])
    void play()
  }

  const {
    playing: isPlaying,
    play,
    togglePlay,
    pause,
    progressLabel,
    seek,
    position,
    next,
    prev,
    duration,
    seeking,
  } = usePlayerControl({
    el: playerRef,
    media: playlist.map(p => p.media),
    active: active?.media,
    onSelect: handleSelect,
    playbackRate,
  })

  const queue = (media: Persisted<Media>) => {
    setPlayList(playlistManager({ playlist, active }).queue({ media, sourceEntityId }))
  }

  /** Schedule play of the current song now */
  const playNow = async (track: Persisted<Media> | Persisted<Media>[]) => {
    const mediaList = Array.isArray(track) ? track : [track]
    const trackList = mediaList.map(media => ({ media, sourceEntityId }))

    setPlayList(playlistManager({ playlist, active }).scheduleNow(trackList))
    const activeTrack = {
      media: Array.isArray(track) ? track[0] : track,
      sourceEntityId,
    }
    setActive(activeTrack)
    return play()
  }

  // Activate a media item already in a playlist and play it
  const activate = async (media: Persisted<Media>) => {
    invariant(
      playlist.some(m => m.media?.id === media?.id),
      'Media item must be in the playlist'
    )
    setActive({ media, sourceEntityId })
    return play()
  }

  return {
    controls: {
      pause,
      seek,
      play,
      togglePlay,
    },
    playlist: {
      tracks: playlist,
      queue,
      playNow,
      activate,
      active,
      next,
      prev,
    },
    isPlaying,
    progressLabel,
    position,
    duration,
    playerRef,
    seeking,
  }
}
